/**
 * Chat Message Reconciler
 *
 * Single source of truth for merging chat messages from any origin (realtime
 * per-row event, full-thread refetch, local streaming, local finalize) into
 * the visible thread. Replaces blind last-writer-wins overwrites that let a
 * lagging DB row re-pin an already-resolved message — the root cause of the
 * observer-stuck-on-loader realtime bug.
 *
 * Primary responsibilities:
 * - Compute a monotonic version per message (terminal latch + LWW tuple)
 * - Reject stale/older incoming rows so newer local state is never clobbered
 * - Protect in-flight streamed content from lagging realtime echoes
 *
 * Dependencies:
 * - lib/chat/pending-assistant-turn (shared terminal-state reader)
 * - lib/chat/message-order
 */

import { getAssistantTurnTerminalState } from '@/lib/chat/pending-assistant-turn'
import { sortChatMessagesByThreadOrder } from '@/lib/chat/message-order'
import type { ChatMessage } from '@/types'

// Where an incoming message came from. Drives local-content protection: a
// realtime/refetch echo must not blank content a local stream is still writing.
export type MergeOrigin = 'realtime' | 'refetch' | 'localStream' | 'localFinalize'

// Monotonic version tuple, compared field-by-field (earlier fields dominate).
interface MessageVersion {
  terminalRank: 0 | 1
  terminalAt: number
  updatedAt: number
  contentLen: number
  id: string
}

// --------------

function getContentLength(message: ChatMessage): number {
  const content = message.message.content
  return typeof content === 'string' ? content.trim().length : 0
}

function parseTimestamp(value: string | null | undefined): number {
  if (!value) return 0
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function getMessageVersion(message: ChatMessage): MessageVersion {
  const { isTerminal, terminalAt } = getAssistantTurnTerminalState(
    message.message.metadata
  )

  return {
    terminalRank: isTerminal ? 1 : 0,
    terminalAt,
    updatedAt: parseTimestamp(message.message.updated_at),
    contentLen: getContentLength(message),
    id: message.message.id
  }
}

// Strictly-greater comparison down the version tuple. Ties -> not newer, so an
// equal update never causes churn.
function isStrictlyNewer(next: MessageVersion, prev: MessageVersion): boolean {
  if (next.terminalRank !== prev.terminalRank) {
    return next.terminalRank > prev.terminalRank
  }
  if (next.terminalAt !== prev.terminalAt) {
    return next.terminalAt > prev.terminalAt
  }
  if (next.updatedAt !== prev.updatedAt) {
    return next.updatedAt > prev.updatedAt
  }
  if (next.contentLen !== prev.contentLen) {
    return next.contentLen > prev.contentLen
  }
  return next.id > prev.id
}

// Carry forward client-only fields the incoming (DB) row may lack.
function carryLocalFields(
  prev: ChatMessage,
  next: ChatMessage,
  keepPrevContent: boolean
): ChatMessage {
  return {
    ...next,
    renderKey: next.renderKey ?? prev.renderKey,
    codeBlocks: keepPrevContent ? prev.codeBlocks : next.codeBlocks ?? prev.codeBlocks,
    fileItems: next.fileItems.length > 0 ? next.fileItems : prev.fileItems,
    ...(keepPrevContent
      ? { message: { ...next.message, content: prev.message.content } }
      : {})
  }
}

// --------------

/**
 * Merge one logical message. `prev` is the currently-held copy, `next` the
 * incoming candidate for the same identity.
 */
export function reconcileMessage(
  prev: ChatMessage | null,
  next: ChatMessage,
  origin: MergeOrigin
): ChatMessage {
  if (!prev) return next

  const prevVersion = getMessageVersion(prev)
  const nextVersion = getMessageVersion(next)

  // RULE 1 — monotonic terminal latch: a resolved (terminal) message is never
  // downgraded to a non-terminal one. This is what stops a lagging
  // streamStatus:'running' realtime row / refetch from re-pinning the observer
  // to the loader after the turn already completed.
  if (prevVersion.terminalRank === 1 && nextVersion.terminalRank === 0) {
    return prev
  }

  // RULE 2 — local content protection: a realtime/refetch echo that carries
  // less content than what we already hold must not blank in-flight or resolved
  // content (e.g. an empty terminal echo, or a lagging chunk behind the local
  // stream). Keep local content while still adopting the incoming row's newer
  // metadata/identity.
  if (
    (origin === 'realtime' || origin === 'refetch') &&
    prevVersion.contentLen > nextVersion.contentLen
  ) {
    return carryLocalFields(prev, next, true)
  }

  // RULE 3 — Last-Write-Wins. Adopt the incoming row unless it is strictly
  // OLDER than what we hold (a stale/out-of-order echo). Equal versions adopt
  // incoming to preserve normal update semantics (e.g. a same-timestamp content
  // edit). The terminal latch (Rule 1) and content protection (Rule 2) above
  // already guard the only cases where adopting a same/older row would be wrong.
  if (isStrictlyNewer(prevVersion, nextVersion)) {
    // prev is strictly newer than next -> incoming is stale, keep prev.
    return prev
  }

  return carryLocalFields(prev, next, false)
}

function matchesIdentity(a: ChatMessage, b: ChatMessage): boolean {
  if (a.message.id === b.message.id) return true
  return (
    a.message.chat_id === b.message.chat_id &&
    a.message.sequence_number === b.message.sequence_number
  )
}

/**
 * Reconcile a batch of incoming messages (one row for realtime, the full thread
 * for refetch) into the current list. The single entry point all writers use.
 */
export function reconcileMessages(
  current: ChatMessage[],
  incoming: ChatMessage[],
  origin: MergeOrigin
): ChatMessage[] {
  if (incoming.length === 0) return current

  const result = [...current]
  let changed = false

  for (const next of incoming) {
    const index = result.findIndex((existing) => matchesIdentity(existing, next))

    if (index === -1) {
      result.push(next)
      changed = true
      continue
    }

    const merged = reconcileMessage(result[index], next, origin)
    if (merged !== result[index]) {
      result[index] = merged
      changed = true
    }
  }

  // Identity stability: when no element actually changed, return the ORIGINAL
  // array reference. A new array on every apply would fail the Object.is guard
  // in the scoped-state setter, so a realtime echo firing ~every 200ms would
  // schedule an endless render->apply cascade (React #185, max update depth).
  if (!changed) {
    return current
  }

  return sortChatMessagesByThreadOrder(result)
}
